/**
 * resourcesProjection — pure helpers that split the flat /api/assets array
 * into the three Resources tabs (Videos / Tutorials / Documents & Files).
 *
 * Extracted in May 2026 to fix the bug where DigitalAssets.jsx projected
 * Videos with `a.id?.startsWith("yt-")`, which silently hid every DB-uploaded
 * (UUID-id) video and emptied the tab on any deploy where the YouTube seed
 * data hadn't been ingested. The new rule projects strictly on `type` /
 * `category`, which is the source of truth and is backward-compatible with
 * all existing yt-* seed records.
 *
 * Kept as a tiny standalone module so the projection has unit tests that
 * don't depend on rendering the (large) DigitalAssets page tree.
 */

/**
 * @param {{name?:string, description?:string}} asset
 * @param {string} q - lowercased search query (or empty)
 */
export function matchesSearch(asset, q) {
  if (!q) return true;
  const name = (asset.name || "").toLowerCase();
  const desc = (asset.description || "").toLowerCase();
  return name.includes(q) || desc.includes(q);
}

/**
 * Project the flat asset list into the three Resources tabs.
 *
 * @param {Array<{id:string,type:string,category?:string,main_category?:string,subcategory?:string}>} assets
 * @param {string} [searchQuery=""]
 * @returns {{ videos:Array, tutorials:Array, docs:Array }}
 */
export function projectResources(assets, searchQuery = "") {
  const list = Array.isArray(assets) ? assets : [];
  const q = (searchQuery || "").toLowerCase();
  const videos = list.filter(a =>
    a && a.type === "video" && a.category !== "tutorial" && matchesSearch(a, q)
  );
  const tutorials = list.filter(a =>
    a && a.category === "tutorial" && matchesSearch(a, q)
  );
  const docs = list.filter(a =>
    a && a.type !== "video" && matchesSearch(a, q)
  );
  return { videos, tutorials, docs };
}
